import {
    PAPER_WHITE_AVATAR_HUE,
    RELIC_RUN_MODE,
    PICTURE_DETECTIVES_MODE,
    PLANET_HOPPERS_MODE,
    VAULT_BREAKERS_MODE,
    type LobbyPlayerView,
    type LobbyPositionView,
    type RoomView
} from "../../shared/game";
import { localizeModeLabel, t } from "./i18n";
import "./browserCompatibility";
import { createCapyShieldElement } from "./capyShield";
import { appendLobbyCapyCostumes, getLobbyCapyCostume } from "./lobbyCapyCostumes";
import { LobbyCanvasRenderer } from "./lobbyCanvasRenderer";
import { LobbyHighFiveTracker, type LobbyHighFiveContact } from "./lobbyHighFive";
import { getLobbyMovementStepPx, LobbyLaunch } from "./lobbyMovement";
import { SITE_TUNING } from "./siteTuning";

type LobbyPlaygroundOptions = {
    interactive?: boolean;
    smoothRemotePlayers?: boolean;
    showViewerNameButton?: boolean;
    showTitle?: boolean;
    hideViewerAvatar?: boolean;
    allowViewerRename?: boolean;
    cameraMode?: "full-board" | "follow-viewer";
    onMove?: (position: LobbyPositionView) => void;
    onEditName?: () => void;
};

type PlayerSprite = {
    element: HTMLDivElement;
    scoreBadge: HTMLElement;
    nameLabel: HTMLElement;
    lastHue: number | null;
    lastName: string;
    lastScore: number | null;
    lastTransform: string;
    lastTitle: string;
    displayPosition: LobbyPositionView | null;
    targetPosition: LobbyPositionView | null;
    pelletExpressionTimeoutId: number | null;
    highFiveTimeoutId: number | null;
    speedBoostTimeoutId: number | null;
    highFiveCooldownTimeoutId: number | null;
};

type RoomViewport = {
    left: number;
    top: number;
    width: number;
    height: number;
    worldWidth: number;
    worldHeight: number;
    cameraLeft: number;
    cameraTop: number;
};

type RenderedLobbyPlayer = {
    player: LobbyPlayerView;
    sprite: PlayerSprite;
    position: LobbyPositionView;
};

const PLAYER_VIEWPORT_SCALE = SITE_TUNING.lobby.camera.playerViewportScale;
const PLAYER_VIEWPORT_SCALE_MOBILE = SITE_TUNING.lobby.camera.playerViewportScaleMobile;
const FOLLOW_CAMERA_DEADZONE_WIDTH_RATIO = SITE_TUNING.lobby.camera.followDeadzoneWidthRatio;
const FOLLOW_CAMERA_DEADZONE_HEIGHT_RATIO = SITE_TUNING.lobby.camera.followDeadzoneHeightRatio;
const POINTER_SNAP_THRESHOLD_PX = SITE_TUNING.lobby.movement.pointerSnapThresholdPx;
const MOVE_SYNC_INTERVAL_MS = SITE_TUNING.lobby.movement.moveSyncIntervalMs;
const POSITION_SYNC_EPSILON = SITE_TUNING.lobby.movement.positionSyncEpsilon;
const LOCAL_POSITION_HARD_SNAP_DISTANCE = SITE_TUNING.lobby.movement.localPositionHardSnapDistance;
const LOCAL_POSITION_EMERGENCY_SNAP_DISTANCE = SITE_TUNING.lobby.movement.localPositionEmergencySnapDistance;
const HARD_SNAP_IDLE_MS = SITE_TUNING.lobby.movement.hardSnapIdleMs;
const SERVER_SETTLE_GRACE_MS = SITE_TUNING.lobby.movement.serverSettleGraceMs;
const SERVER_SETTLE_EPSILON_PX = SITE_TUNING.lobby.movement.serverSettleEpsilonPx;
const SERVER_CORRECTION_SPEED_PX_PER_SECOND = SITE_TUNING.lobby.movement.serverCorrectionSpeedPxPerSecond;
const REMOTE_SMOOTHING_MS = SITE_TUNING.lobby.movement.remoteSmoothingMs;
const REMOTE_HARD_SNAP_DISTANCE = SITE_TUNING.lobby.movement.remoteHardSnapDistance;
const SPECTATOR_RENDER_INTERVAL_MS = SITE_TUNING.lobby.movement.spectatorRenderIntervalMs;
const SPECTATOR_IDLE_RENDER_INTERVAL_MS = SITE_TUNING.lobby.movement.spectatorIdleRenderIntervalMs;
const LOBBY_MIN_X = SITE_TUNING.lobby.bounds.minX;
const LOBBY_MAX_X = SITE_TUNING.lobby.bounds.maxX;
const LOBBY_MIN_Y = SITE_TUNING.lobby.bounds.minY;
const LOBBY_MAX_Y = SITE_TUNING.lobby.bounds.maxY;
const PELLET_SPLASH_POSITION_DELTA = SITE_TUNING.lobby.movement.pelletSplashPositionDelta;
const PELLET_EXPRESSION_DURATION_MS = SITE_TUNING.lobby.feedback.pelletExpressionDurationMs;
const HIGH_FIVE_TOUCH_DISTANCE_PX = SITE_TUNING.lobby.feedback.highFiveTouchDistancePx;
const HIGH_FIVE_COOLDOWN_MS = SITE_TUNING.lobby.feedback.highFiveCooldownMs;
const HIGH_FIVE_EFFECT_DURATION_MS = SITE_TUNING.lobby.feedback.highFiveEffectDurationMs;
const HIGH_FIVE_SPEED_BOOST_DURATION_MS = SITE_TUNING.lobby.feedback.highFiveSpeedBoostDurationMs;

const clamp = (value: number, minimum: number, maximum: number): number =>
    Math.min(maximum, Math.max(minimum, value));

const distanceBetween = (left: LobbyPositionView, right: LobbyPositionView): number =>
    Math.hypot(left.x - right.x, left.y - right.y);

const getWaitingModeLabel = (mode: RoomView["mode"]): string =>
    localizeModeLabel(mode) ?? t("hostChoosing");

export class LobbyPlayground {
    private readonly root: HTMLElement;
    private readonly options: LobbyPlaygroundOptions;
    private readonly stageHead: HTMLElement;
    private readonly titleLabel: HTMLElement;
    private readonly viewerNameButton: HTMLButtonElement;
    private readonly viewerStatusLabel: HTMLElement;
    private readonly viewerNameLabel: HTMLElement;
    private readonly roomCodeLabel: HTMLElement;
    private readonly titleTextLabel: HTMLElement;
    private readonly modeLabel: HTMLElement;
    private readonly stage: HTMLElement;
    private readonly world: HTMLElement;
    private readonly worldCanvas: HTMLCanvasElement;
    private readonly worldRenderer: LobbyCanvasRenderer;
    private readonly effectsLayer: HTMLElement;
    private readonly avatarLayer: HTMLElement;
    private readonly pelletPositions = new Map<string, LobbyPositionView>();
    private readonly playerElements = new Map<string, PlayerSprite>();
    private readonly highFiveTracker = new LobbyHighFiveTracker(HIGH_FIVE_COOLDOWN_MS);
    private readonly pressedKeys = new Set<string>();
    private readonly onKeyDown = (event: KeyboardEvent) => {
        if (this.isEditableTarget(event.target)) {
            return;
        }

        const key = this.normalizeMovementKey(event.key);

        if (!key) {
            return;
        }

        this.pressedKeys.add(key);
        event.preventDefault();
    };
    private readonly onKeyUp = (event: KeyboardEvent) => {
        if (this.isEditableTarget(event.target)) {
            return;
        }

        const key = this.normalizeMovementKey(event.key);

        if (!key) {
            return;
        }

        this.pressedKeys.delete(key);
    };
    private readonly onWindowBlur = () => {
        this.pressedKeys.clear();
        this.target = null;
        this.targetAllowsSnap = true;
    };
    private readonly isInteractive: boolean;
    private readonly smoothRemotePlayers: boolean;
    private readonly allowViewerRename: boolean;
    private readonly cameraMode: "full-board" | "follow-viewer";
    private room: RoomView | null = null;
    private authoritativePosition: LobbyPositionView | null = null;
    private localPosition: LobbyPositionView | null = null;
    private target: LobbyPositionView | null = null;
    private targetAllowsSnap = true;
    private lastSentPosition: LobbyPositionView | null = null;
    private lastSentAt = 0;
    private lastLocalMoveAt = 0;
    private lastServerSyncAt = 0;
    private followCameraLeft = 0;
    private followCameraTop = 0;
    private followCameraReady = false;
    private animationFrameId = 0;
    private lastFrameAt = 0;
    private lastRenderAt = 0;
    private lastWorldLayoutStyle = "";
    private viewerSpeedBoostEndsAt = 0;
    private readonly viewerLaunch = new LobbyLaunch();

    constructor(root: HTMLElement, options: LobbyPlaygroundOptions = {}) {
        this.root = root;
        this.options = options;
        this.isInteractive = options.interactive !== false;
        this.smoothRemotePlayers = options.smoothRemotePlayers ?? !this.isInteractive;
        this.allowViewerRename = options.allowViewerRename !== false;
        this.cameraMode = options.cameraMode ?? "full-board";
        this.root.innerHTML = `
            <div class="lobby-playground-shell">
                <div
                    class="lobby-playground-stage"
                    tabindex="${this.isInteractive ? "0" : "-1"}"
                    aria-label="${this.escapeAttribute(this.buildStageAriaLabel())}"
                >
                    <div class="lobby-playground-stage-head">
                        <button
                            class="lobby-playground-name-button"
                            type="button"
                            title="${this.escapeAttribute(t("editName"))}"
                            aria-label="${this.escapeAttribute(t("editName"))}"
                        >
                            <span class="lobby-playground-viewer-status"></span>
                            <span class="lobby-playground-viewer-name"></span>
                            <span class="lobby-playground-edit-icon" aria-hidden="true">&#9998;</span>
                        </button>
                        <p class="lobby-playground-title">
                            <strong class="lobby-playground-room-code"></strong>
                            <span class="lobby-playground-title-text">${this.escapeAttribute(
                                t("waitingRoom")
                            )}</span>
                            <span class="lobby-playground-title-divider" aria-hidden="true">-</span>
                            <span class="lobby-playground-mode"></span>
                        </p>
                        <span class="lobby-playground-stage-spacer" aria-hidden="true"></span>
                    </div>
                    <div class="lobby-playground-world">
                        <canvas class="lobby-playground-canvas" aria-hidden="true"></canvas>
                        <div class="lobby-playground-effects" aria-hidden="true"></div>
                        <div class="lobby-playground-avatars"></div>
                    </div>
                </div>
            </div>
        `;

        this.stageHead = this.root.querySelector<HTMLElement>(".lobby-playground-stage-head")!;
        this.titleLabel = this.root.querySelector<HTMLElement>(".lobby-playground-title")!;
        this.viewerNameButton = this.root.querySelector<HTMLButtonElement>(
            ".lobby-playground-name-button"
        )!;
        this.viewerStatusLabel = this.root.querySelector<HTMLElement>(".lobby-playground-viewer-status")!;
        this.viewerNameLabel = this.root.querySelector<HTMLElement>(".lobby-playground-viewer-name")!;
        this.roomCodeLabel = this.root.querySelector<HTMLElement>(".lobby-playground-room-code")!;
        this.titleTextLabel = this.root.querySelector<HTMLElement>(".lobby-playground-title-text")!;
        this.modeLabel = this.root.querySelector<HTMLElement>(".lobby-playground-mode")!;
        this.stage = this.root.querySelector<HTMLElement>(".lobby-playground-stage")!;
        this.world = this.root.querySelector<HTMLElement>(".lobby-playground-world")!;
        this.worldCanvas = this.root.querySelector<HTMLCanvasElement>(".lobby-playground-canvas")!;
        // Keep the interactive avatars in DOM while the canvas owns the ambient waiting-room scene.
        this.worldRenderer = new LobbyCanvasRenderer(this.worldCanvas);
        this.effectsLayer = this.root.querySelector<HTMLElement>(".lobby-playground-effects")!;
        this.avatarLayer = this.root.querySelector<HTMLElement>(".lobby-playground-avatars")!;
        this.titleLabel.setAttribute("aria-label", t("waitingRoom"));
        this.modeLabel.textContent = getWaitingModeLabel(null);
        if (this.worldRenderer.isAvailable) {
            this.stage.classList.add("is-canvas-rendered");
        }
        if (!this.isInteractive) {
            this.stage.classList.add("is-spectator");
        }
        if (this.cameraMode === "follow-viewer") {
            this.stage.classList.add("is-follow-camera");
        }
        if (options.showViewerNameButton === false) {
            this.viewerNameButton.hidden = true;
        }
        this.viewerNameButton.classList.toggle("is-readonly", !this.allowViewerRename);
        this.viewerNameButton.setAttribute("aria-disabled", String(!this.allowViewerRename));
        this.viewerNameButton.tabIndex = this.allowViewerRename ? 0 : -1;
        if (options.showTitle === false) {
            this.titleLabel.hidden = true;
        }
        if (options.showViewerNameButton === false && options.showTitle === false) {
            this.stageHead.hidden = true;
        }

        this.viewerNameButton.addEventListener("pointerdown", (event) => {
            event.stopPropagation();
        });
        this.viewerNameButton.addEventListener("click", (event) => {
            event.stopPropagation();
            if (this.allowViewerRename) {
                this.options.onEditName?.();
            }
        });

        if (this.isInteractive) {
            this.stage.addEventListener("pointerdown", (event) => {
                if (event.button !== 0) {
                    return;
                }

                event.preventDefault();
                this.stage.focus({ preventScroll: true });
                const target = this.normalizePointer(event);
                this.target = target;
                this.targetAllowsSnap = true;
                this.worldRenderer.emitMoveTarget(target);
            });
            this.stage.addEventListener("pointermove", (event) => {
                if ((event.buttons & 1) === 0) {
                    return;
                }

                event.preventDefault();
                this.target = this.normalizePointer(event);
                this.targetAllowsSnap = false;
            });
            this.stage.addEventListener("click", (event) => {
                event.preventDefault();

                // A real pointer click was already handled on pointerdown; only preserve synthetic clicks here.
                if (event.detail > 0) {
                    return;
                }

                const target = this.normalizePointer(event);
                this.target = target;
                this.targetAllowsSnap = true;
                this.worldRenderer.emitMoveTarget(target);
            });

            window.addEventListener("keydown", this.onKeyDown);
            window.addEventListener("keyup", this.onKeyUp);
            window.addEventListener("blur", this.onWindowBlur);
        }

        this.animationFrameId = window.requestAnimationFrame(this.tick);
        if (this.isInteractive) {
            window.requestAnimationFrame(() => {
                this.stage.focus({ preventScroll: true });
            });
        }
    }

    setRoom(room: RoomView) {
        if (this.room?.roomCode !== room.roomCode) {
            this.pelletPositions.clear();
            this.worldRenderer.clearTransientState();
            this.highFiveTracker.reset();
            this.resetFollowCamera();
            this.lastRenderAt = 0;
            this.viewerSpeedBoostEndsAt = 0;
            this.viewerLaunch.reset();
        }

        this.room = room;
        this.viewerStatusLabel.textContent = this.allowViewerRename ? "" : t("joined");
        this.viewerNameLabel.textContent = room.viewer.name;
        if (this.allowViewerRename) {
            this.viewerNameButton.setAttribute("title", t("editName"));
            this.viewerNameButton.setAttribute("aria-label", `${t("editName")}: ${room.viewer.name}`);
        } else {
            this.viewerNameButton.removeAttribute("title");
            this.viewerNameButton.setAttribute("aria-label", `${t("joined")}: ${room.viewer.name}`);
        }
        this.roomCodeLabel.textContent = room.roomCode;
        this.titleTextLabel.textContent = t("waitingRoom");
        this.modeLabel.textContent = getWaitingModeLabel(room.mode);
        this.root.dataset.lobbyCapyCostume = getLobbyCapyCostume(room.mode);
        this.stage.setAttribute("aria-label", this.buildStageAriaLabel(room));
        this.worldRenderer.setTheme(
            room.mode === PICTURE_DETECTIVES_MODE
                ? "pictionary"
                : room.mode === RELIC_RUN_MODE
                    ? "relic"
                    : room.mode === VAULT_BREAKERS_MODE
                        ? "vault"
                        : room.mode === PLANET_HOPPERS_MODE
                            ? "planet"
                            : "default"
        );

        this.syncPellets(room);
        this.syncPlayers(room);

        const viewer = this.getViewerPlayer(room);

        if (!viewer) {
            this.viewerSpeedBoostEndsAt = 0;
            this.viewerLaunch.reset();
            this.authoritativePosition = null;
            this.localPosition = null;
            this.target = null;
            this.lastSentPosition = null;
            this.resetFollowCamera();
            this.render();
            return;
        }

        const now = performance.now();
        const previousAuthoritative = this.authoritativePosition;
        this.authoritativePosition = {
            x: viewer.position.x,
            y: viewer.position.y
        };

        if (
            !previousAuthoritative ||
            distanceBetween(previousAuthoritative, this.authoritativePosition) >= POSITION_SYNC_EPSILON
        ) {
            this.lastServerSyncAt = now;
        }

        const divergence = this.localPosition
            ? distanceBetween(this.localPosition, this.authoritativePosition)
            : Number.POSITIVE_INFINITY;
        const shouldSnapToServer =
            !this.localPosition ||
            divergence >= LOCAL_POSITION_EMERGENCY_SNAP_DISTANCE ||
            (!this.hasMovementIntent() &&
                now - this.lastLocalMoveAt >= HARD_SNAP_IDLE_MS &&
                divergence >= LOCAL_POSITION_HARD_SNAP_DISTANCE);

        if (shouldSnapToServer) {
            this.localPosition = {
                x: viewer.position.x,
                y: viewer.position.y
            };
            this.target = null;
            this.targetAllowsSnap = true;
            this.lastSentPosition = {
                x: viewer.position.x,
                y: viewer.position.y
            };
        }

        this.render();
    }

    destroy() {
        if (this.animationFrameId) {
            window.cancelAnimationFrame(this.animationFrameId);
        }

        this.worldRenderer.destroy();
        window.removeEventListener("keydown", this.onKeyDown);
        window.removeEventListener("keyup", this.onKeyUp);
        window.removeEventListener("blur", this.onWindowBlur);
        for (const sprite of this.playerElements.values()) {
            this.clearPlayerSpriteTimers(sprite);
        }
        this.playerElements.clear();
        this.pelletPositions.clear();
        this.highFiveTracker.reset();
        this.root.innerHTML = "";
    }

    private readonly tick = (timestamp: number) => {
        if (!this.lastFrameAt) {
            this.lastFrameAt = timestamp;
        }

        const deltaSeconds = Math.min((timestamp - this.lastFrameAt) / 1000, 0.045);
        this.lastFrameAt = timestamp;

        this.step(deltaSeconds, timestamp);
        if (this.shouldRenderFrame(timestamp)) {
            this.render(timestamp);
        }
        this.animationFrameId = window.requestAnimationFrame(this.tick);
    };

    private step(deltaSeconds: number, timestamp: number) {
        if (!this.localPosition) {
            return;
        }

        const viewport = this.getRoomViewport();
        let moveX = 0;
        let moveY = 0;
        let snappedToTarget = false;

        if (this.pressedKeys.size > 0) {
            moveX += this.pressedKeys.has("right") ? 1 : 0;
            moveX -= this.pressedKeys.has("left") ? 1 : 0;
            moveY += this.pressedKeys.has("down") ? 1 : 0;
            moveY -= this.pressedKeys.has("up") ? 1 : 0;
        } else if (this.target) {
            const deltaX = (this.target.x - this.localPosition.x) * viewport.worldWidth;
            const deltaY = (this.target.y - this.localPosition.y) * viewport.worldHeight;
            const distance = Math.hypot(deltaX, deltaY);

            if (!this.viewerLaunch.active && this.targetAllowsSnap && distance <= POINTER_SNAP_THRESHOLD_PX) {
                this.localPosition = {
                    x: this.target.x,
                    y: this.target.y
                };
                this.target = null;
                this.targetAllowsSnap = true;
                snappedToTarget = true;
            } else {
                moveX = deltaX;
                moveY = deltaY;
            }
        }

        // Steering must not replace the momentum direction used by another high-five.
        if (!this.viewerLaunch.active) this.viewerLaunch.remember(moveX, moveY, timestamp);
        const impulse = this.viewerLaunch.step(deltaSeconds);
        const launching = impulse.x !== 0 || impulse.y !== 0;
        if (launching) {
            this.localPosition = {
                x: clamp(this.localPosition.x + impulse.x / viewport.worldWidth, LOBBY_MIN_X, LOBBY_MAX_X),
                y: clamp(this.localPosition.y + impulse.y / viewport.worldHeight, LOBBY_MIN_Y, LOBBY_MAX_Y)
            };
            this.lastLocalMoveAt = timestamp;
            this.sendMoveIfNeeded(timestamp);
        }

        if (moveX === 0 && moveY === 0) {
            if (snappedToTarget) {
                this.lastLocalMoveAt = timestamp;
                this.sendMoveIfNeeded(timestamp, true);
            } else if (!launching) {
                this.reconcileToServer(deltaSeconds, timestamp, viewport);
            }

            return;
        }

        const vectorLength = Math.hypot(moveX, moveY) || 1;
        const speedPx = getLobbyMovementStepPx(deltaSeconds, timestamp < this.viewerSpeedBoostEndsAt) *
            (launching ? SITE_TUNING.lobby.feedback.highFiveLaunchSteeringMultiplier : 1);

        this.localPosition = {
            x: clamp(
                this.localPosition.x + ((moveX / vectorLength) * speedPx) / viewport.worldWidth,
                LOBBY_MIN_X,
                LOBBY_MAX_X
            ),
            y: clamp(
                this.localPosition.y + ((moveY / vectorLength) * speedPx) / viewport.worldHeight,
                LOBBY_MIN_Y,
                LOBBY_MAX_Y
            )
        };

        this.lastLocalMoveAt = timestamp;
        this.sendMoveIfNeeded(timestamp);
    }

    private sendMoveIfNeeded(timestamp: number, force = false) {
        if (!this.localPosition) {
            return;
        }

        const changedEnough =
            !this.lastSentPosition ||
            distanceBetween(this.localPosition, this.lastSentPosition) >= POSITION_SYNC_EPSILON;

        if (!changedEnough) {
            return;
        }

        if (!force && timestamp - this.lastSentAt < MOVE_SYNC_INTERVAL_MS) {
            return;
        }

        this.options.onMove?.({
            x: this.localPosition.x,
            y: this.localPosition.y
        });
        this.lastSentPosition = {
            x: this.localPosition.x,
            y: this.localPosition.y
        };
        this.lastSentAt = timestamp;
    }

    private reconcileToServer(deltaSeconds: number, timestamp: number, viewport: RoomViewport) {
        if (!this.localPosition || !this.authoritativePosition) {
            return;
        }

        if (this.hasMovementIntent()) {
            return;
        }

        const quietSince = Math.max(this.lastLocalMoveAt, this.lastServerSyncAt);

        if (timestamp - quietSince < SERVER_SETTLE_GRACE_MS) {
            return;
        }

        const deltaXPx = (this.authoritativePosition.x - this.localPosition.x) * viewport.worldWidth;
        const deltaYPx = (this.authoritativePosition.y - this.localPosition.y) * viewport.worldHeight;
        const distancePx = Math.hypot(deltaXPx, deltaYPx);

        if (distancePx <= SERVER_SETTLE_EPSILON_PX) {
            this.localPosition = {
                x: this.authoritativePosition.x,
                y: this.authoritativePosition.y
            };
            return;
        }

        const correctionStepPx = SERVER_CORRECTION_SPEED_PX_PER_SECOND * deltaSeconds;

        if (distancePx <= correctionStepPx) {
            this.localPosition = {
                x: this.authoritativePosition.x,
                y: this.authoritativePosition.y
            };
            return;
        }

        const stepRatio = correctionStepPx / distancePx;

        this.localPosition = {
            x: clamp(
                this.localPosition.x + (deltaXPx * stepRatio) / viewport.worldWidth,
                LOBBY_MIN_X,
                LOBBY_MAX_X
            ),
            y: clamp(
                this.localPosition.y + (deltaYPx * stepRatio) / viewport.worldHeight,
                LOBBY_MIN_Y,
                LOBBY_MAX_Y
            )
        };
    }

    private syncPellets(room: RoomView) {
        const pellets = room.lobbyPlayground?.pellets ?? [];
        const nextIds = new Set(pellets.map((pellet) => pellet.id));

        for (const id of this.pelletPositions.keys()) {
            if (nextIds.has(id)) {
                continue;
            }

            this.pelletPositions.delete(id);
        }

        this.worldRenderer.setPellets(pellets);
    }

    private syncPlayers(room: RoomView) {
        const players = this.getRenderablePlayers(room);
        const nextIds = new Set(players.map((player) => player.sessionId));

        for (const [sessionId, sprite] of this.playerElements) {
            if (nextIds.has(sessionId)) {
                continue;
            }

            this.clearPlayerSpriteTimers(sprite);
            sprite.element.remove();
            this.playerElements.delete(sessionId);
        }

        for (const player of players) {
            let sprite = this.playerElements.get(player.sessionId);

            if (!sprite) {
                sprite = this.createPlayerSprite();
                this.avatarLayer.appendChild(sprite.element);
                this.playerElements.set(player.sessionId, sprite);
            }

            const isSelf = player.sessionId === room.viewer.sessionId;
            const nextTitle = `${player.name}: ${player.score}`;
            const nextPosition = {
                x: player.position.x,
                y: player.position.y
            };

            sprite.targetPosition = nextPosition;

            if (!sprite.displayPosition || !this.shouldSmoothPlayerSprite(room, player)) {
                sprite.displayPosition = nextPosition;
            }

            sprite.element.classList.toggle("is-self", isSelf);

            const isPaperWhite = player.avatarHue === PAPER_WHITE_AVATAR_HUE;
            sprite.element.classList.toggle("is-paper-white", isPaperWhite);

            if (sprite.lastHue !== player.avatarHue) {
                if (!isPaperWhite) {
                    sprite.element.style.setProperty("--playground-avatar-hue", String(player.avatarHue));
                }
                sprite.lastHue = player.avatarHue;
            }

            if (sprite.lastScore !== player.score) {
                // Initial room hydration stays calm; only a confirmed score gain gets the collection face.
                if (sprite.lastScore !== null && player.score > sprite.lastScore) {
                    this.animatePelletCollection(sprite);
                }
                sprite.scoreBadge.textContent = String(player.score);
                sprite.lastScore = player.score;
            }

            if (sprite.lastName !== player.name) {
                sprite.nameLabel.textContent = player.name;
                sprite.lastName = player.name;
            }

            if (sprite.lastTitle !== nextTitle) {
                sprite.element.setAttribute("title", nextTitle);
                sprite.lastTitle = nextTitle;
            }
        }
    }

    private createPlayerSprite(): PlayerSprite {
        const element = document.createElement("div");
        const scoreBadge = document.createElement("span");
        const square = createCapyShieldElement();
        const speedBoostEffect = document.createElement("span");
        const highFiveCooldown = document.createElement("span");
        const nameLabel = document.createElement("span");

        element.className = "lobby-playground-avatar";
        scoreBadge.className = "lobby-playground-score";
        speedBoostEffect.className = "lobby-speed-boost-effect";
        speedBoostEffect.setAttribute("aria-hidden", "true");
        highFiveCooldown.className = "lobby-high-five-cooldown";
        highFiveCooldown.setAttribute("aria-hidden", "true");
        nameLabel.className = "lobby-playground-name";
        appendLobbyCapyCostumes(square);

        element.append(speedBoostEffect, scoreBadge, square, highFiveCooldown, nameLabel);

        return {
            element,
            scoreBadge,
            nameLabel,
            lastHue: null,
            lastName: "",
            lastScore: null,
            lastTransform: "",
            lastTitle: "",
            displayPosition: null,
            targetPosition: null,
            pelletExpressionTimeoutId: null,
            highFiveTimeoutId: null,
            speedBoostTimeoutId: null,
            highFiveCooldownTimeoutId: null
        };
    }

    private animatePelletCollection(sprite: PlayerSprite) {
        if (sprite.pelletExpressionTimeoutId !== null) {
            window.clearTimeout(sprite.pelletExpressionTimeoutId);
        }

        sprite.element.classList.remove("is-pellet-celebrating");
        void sprite.element.offsetWidth;
        sprite.element.classList.add("is-pellet-celebrating");
        sprite.pelletExpressionTimeoutId = window.setTimeout(() => {
            sprite.element.classList.remove("is-pellet-celebrating");
            sprite.pelletExpressionTimeoutId = null;
        }, PELLET_EXPRESSION_DURATION_MS);
    }

    private shouldSmoothPlayerSprite(room: RoomView, player: LobbyPlayerView): boolean {
        return this.smoothRemotePlayers && player.sessionId !== room.viewer.sessionId;
    }

    private getSmoothedPlayerPosition(
        sprite: PlayerSprite,
        targetPosition: LobbyPositionView,
        shouldSmooth: boolean,
        deltaSeconds: number
    ): LobbyPositionView {
        if (!shouldSmooth || !sprite.displayPosition) {
            sprite.displayPosition = targetPosition;
            return targetPosition;
        }

        if (distanceBetween(sprite.displayPosition, targetPosition) >= REMOTE_HARD_SNAP_DISTANCE) {
            sprite.displayPosition = targetPosition;
            return targetPosition;
        }

        const smoothingAlpha =
            deltaSeconds <= 0 || REMOTE_SMOOTHING_MS <= 0
                ? 0
                : 1 - Math.exp(-(deltaSeconds * 1000) / REMOTE_SMOOTHING_MS);
        const nextPosition = {
            x: sprite.displayPosition.x + (targetPosition.x - sprite.displayPosition.x) * smoothingAlpha,
            y: sprite.displayPosition.y + (targetPosition.y - sprite.displayPosition.y) * smoothingAlpha
        };

        if (distanceBetween(nextPosition, targetPosition) < POSITION_SYNC_EPSILON) {
            sprite.displayPosition = targetPosition;
            return targetPosition;
        }

        sprite.displayPosition = nextPosition;
        return nextPosition;
    }

    private render(timestamp = performance.now()) {
        const room = this.room;

        if (!room) {
            return;
        }

        const viewport = this.getRoomViewport();
        const deltaSeconds = this.lastRenderAt
            ? Math.min(Math.max((timestamp - this.lastRenderAt) / 1000, 0), 0.045)
            : 0;
        this.lastRenderAt = timestamp;
        this.layoutWorld(viewport);
        this.worldRenderer.setViewport(viewport);

        for (const pellet of room.lobbyPlayground?.pellets ?? []) {
            const nextPosition = { x: pellet.x, y: pellet.y };
            const previousPosition = this.pelletPositions.get(pellet.id);

            if (
                previousPosition &&
                distanceBetween(previousPosition, nextPosition) >= PELLET_SPLASH_POSITION_DELTA
            ) {
                this.emitPelletSplash(previousPosition, timestamp);
            }

            this.pelletPositions.set(pellet.id, nextPosition);
        }

        const renderedPlayers: RenderedLobbyPlayer[] = [];

        for (const player of this.getRenderablePlayers(room)) {
            const sprite = this.playerElements.get(player.sessionId);

            if (!sprite) {
                continue;
            }

            const position =
                player.sessionId === room.viewer.sessionId && this.localPosition
                    ? this.localPosition
                    : this.getSmoothedPlayerPosition(
                          sprite,
                          sprite.targetPosition ?? player.position,
                          this.shouldSmoothPlayerSprite(room, player),
                          deltaSeconds
                      );

            const transform = `translate(${(position.x * viewport.worldWidth - viewport.cameraLeft).toFixed(
                1
            )}px, ${(position.y * viewport.worldHeight - viewport.cameraTop).toFixed(1)}px) translate(-50%, -50%)`;

            if (sprite.lastTransform !== transform) {
                sprite.element.style.transform = transform;
                sprite.lastTransform = transform;
            }

            renderedPlayers.push({ player, sprite, position });
        }

        this.syncHighFives(renderedPlayers, viewport, timestamp);
        this.worldRenderer.render(timestamp);
    }

    private syncHighFives(
        renderedPlayers: RenderedLobbyPlayer[],
        viewport: RoomViewport,
        timestamp: number
    ) {
        const contacts: LobbyHighFiveContact[] = [];
        const playersBySessionId = new Map(
            renderedPlayers.map((renderedPlayer) => [renderedPlayer.player.sessionId, renderedPlayer])
        );

        for (let leftIndex = 0; leftIndex < renderedPlayers.length; leftIndex += 1) {
            const left = renderedPlayers[leftIndex];

            for (let rightIndex = leftIndex + 1; rightIndex < renderedPlayers.length; rightIndex += 1) {
                const right = renderedPlayers[rightIndex];
                const distancePx = Math.hypot(
                    (left.position.x - right.position.x) * viewport.worldWidth,
                    (left.position.y - right.position.y) * viewport.worldHeight
                );

                if (distancePx <= HIGH_FIVE_TOUCH_DISTANCE_PX) {
                    contacts.push([left.player.sessionId, right.player.sessionId]);
                }
            }
        }

        for (const event of this.highFiveTracker.update(contacts, timestamp)) {
            const left = playersBySessionId.get(event.leftSessionId);
            const right = playersBySessionId.get(event.rightSessionId);

            if (!left || !right) {
                continue;
            }

            const midpoint = {
                x: (left.position.x + right.position.x) / 2,
                y: (left.position.y + right.position.y) / 2
            };
            this.worldRenderer.emitHighFive(
                midpoint,
                [left.player.avatarHue, right.player.avatarHue],
                timestamp
            );
            this.emitHighFiveBurst(
                midpoint,
                [left.player.avatarHue, right.player.avatarHue],
                viewport
            );
            this.animateHighFivePlayer(left, right, viewport);
            this.animateHighFivePlayer(right, left, viewport);
            this.activateHighFiveSpeedBoost(left, timestamp);
            this.activateHighFiveSpeedBoost(right, timestamp);
            const viewerId = this.room?.viewer.sessionId;
            if (left.player.sessionId === viewerId) this.activateHighFiveCooldownIndicator(right.sprite);
            if (right.player.sessionId === viewerId) this.activateHighFiveCooldownIndicator(left.sprite);
        }
    }

    private activateHighFiveCooldownIndicator(sprite: PlayerSprite) {
        if (sprite.highFiveCooldownTimeoutId !== null) {
            window.clearTimeout(sprite.highFiveCooldownTimeoutId);
        }

        sprite.element.style.setProperty("--lobby-high-five-cooldown-duration", `${HIGH_FIVE_COOLDOWN_MS}ms`);
        sprite.element.classList.remove("is-high-five-cooling-down");
        void sprite.element.offsetWidth;
        sprite.element.classList.add("is-high-five-cooling-down");
        sprite.highFiveCooldownTimeoutId = window.setTimeout(() => {
            sprite.element.classList.remove("is-high-five-cooling-down");
            sprite.highFiveCooldownTimeoutId = null;
        }, HIGH_FIVE_COOLDOWN_MS);
    }

    private activateHighFiveSpeedBoost(player: RenderedLobbyPlayer, timestamp: number) {
        const sprite = player.sprite;
        if (sprite.speedBoostTimeoutId !== null) {
            window.clearTimeout(sprite.speedBoostTimeoutId);
        }

        sprite.element.classList.remove("is-speed-boosted");
        void sprite.element.offsetWidth;
        sprite.element.classList.add("is-speed-boosted");
        sprite.speedBoostTimeoutId = window.setTimeout(() => {
            sprite.element.classList.remove("is-speed-boosted");
            sprite.speedBoostTimeoutId = null;
        }, HIGH_FIVE_SPEED_BOOST_DURATION_MS);

        if (this.isInteractive && player.player.sessionId === this.room?.viewer.sessionId) {
            // Discard the old pointer destination so it cannot immediately pull the launch back.
            this.target = null;
            this.viewerLaunch.launch(timestamp);
            this.viewerSpeedBoostEndsAt = Math.max(
                this.viewerSpeedBoostEndsAt,
                timestamp + HIGH_FIVE_SPEED_BOOST_DURATION_MS
            );
        }
    }

    private animateHighFivePlayer(
        player: RenderedLobbyPlayer,
        otherPlayer: RenderedLobbyPlayer,
        viewport: RoomViewport
    ) {
        const deltaX = (otherPlayer.position.x - player.position.x) * viewport.worldWidth;
        const deltaY = (otherPlayer.position.y - player.position.y) * viewport.worldHeight;
        const length = Math.hypot(deltaX, deltaY) || 1;
        const sprite = player.sprite.element;

        sprite.style.setProperty("--lobby-high-five-x", `${((deltaX / length) * -4).toFixed(1)}px`);
        sprite.style.setProperty("--lobby-high-five-y", `${((deltaY / length) * -4).toFixed(1)}px`);
        if (player.sprite.highFiveTimeoutId !== null) {
            window.clearTimeout(player.sprite.highFiveTimeoutId);
        }
        sprite.classList.remove("is-high-fiving");
        void sprite.offsetWidth;
        sprite.classList.add("is-high-fiving");
        player.sprite.highFiveTimeoutId = window.setTimeout(() => {
            sprite.classList.remove("is-high-fiving");
            player.sprite.highFiveTimeoutId = null;
        }, HIGH_FIVE_EFFECT_DURATION_MS);
    }

    private clearPlayerSpriteTimers(sprite: PlayerSprite) {
        if (sprite.pelletExpressionTimeoutId !== null) {
            window.clearTimeout(sprite.pelletExpressionTimeoutId);
        }
        if (sprite.highFiveTimeoutId !== null) {
            window.clearTimeout(sprite.highFiveTimeoutId);
        }
        if (sprite.speedBoostTimeoutId !== null) {
            window.clearTimeout(sprite.speedBoostTimeoutId);
        }
        if (sprite.highFiveCooldownTimeoutId !== null) {
            window.clearTimeout(sprite.highFiveCooldownTimeoutId);
        }
    }

    private emitHighFiveBurst(
        position: LobbyPositionView,
        avatarHues: readonly [number, number],
        viewport: RoomViewport
    ) {
        const burst = document.createElement("span");
        burst.className = "lobby-high-five-burst";
        burst.style.left = `${position.x * viewport.worldWidth - viewport.cameraLeft}px`;
        burst.style.top = `${position.y * viewport.worldHeight - viewport.cameraTop}px`;

        for (let index = 0; index < 10; index += 1) {
            const ray = document.createElement("i");
            const avatarHue = avatarHues[index % 2];
            ray.className = "lobby-high-five-ray";
            ray.style.setProperty("--lobby-high-five-angle", `${index * 36}deg`);
            ray.style.setProperty(
                "--lobby-high-five-color",
                avatarHue < 0 ? "rgba(248, 252, 255, 0.96)" : `hsl(${avatarHue} 94% 72%)`
            );
            burst.appendChild(ray);
        }

        this.effectsLayer.appendChild(burst);
        window.setTimeout(() => {
            burst.remove();
        }, HIGH_FIVE_EFFECT_DURATION_MS);
    }

    private shouldRenderFrame(timestamp: number): boolean {
        if (this.isInteractive || !this.lastRenderAt) {
            return true;
        }

        const intervalMs =
            this.hasSmoothedPlayerMovement() || this.worldRenderer.hasActiveEffects(timestamp)
                ? SPECTATOR_RENDER_INTERVAL_MS
                : SPECTATOR_IDLE_RENDER_INTERVAL_MS;

        return timestamp - this.lastRenderAt >= intervalMs;
    }

    private hasSmoothedPlayerMovement(): boolean {
        if (!this.smoothRemotePlayers) {
            return false;
        }

        for (const sprite of this.playerElements.values()) {
            if (
                sprite.displayPosition &&
                sprite.targetPosition &&
                distanceBetween(sprite.displayPosition, sprite.targetPosition) >= POSITION_SYNC_EPSILON
            ) {
                return true;
            }
        }

        return false;
    }

    private getViewerPlayer(room = this.room): LobbyPlayerView | null {
        if (!room?.lobbyPlayground) {
            return null;
        }

        return (
            room.lobbyPlayground.players.find((player) => player.sessionId === room.viewer.sessionId) ??
            null
        );
    }

    private getRenderablePlayers(room = this.room): LobbyPlayerView[] {
        const hostIds = new Set(room?.participants?.filter((participant) => participant.isHost).map((participant) => participant.sessionId));
        if (room?.viewer.isHost) hostIds.add(room.viewer.sessionId);
        const players = (room?.lobbyPlayground?.players ?? []).filter((player) => !hostIds.has(player.sessionId));

        if (!room || !this.options.hideViewerAvatar) {
            return players;
        }

        return players.filter((player) => player.sessionId !== room.viewer.sessionId);
    }

    private buildStageAriaLabel(room = this.room): string {
        const parts: string[] = [];

        if (room?.viewer.name) {
            parts.push(room.viewer.name);
        }

        if (room?.roomCode) {
            parts.push(room.roomCode);
        }

        parts.push(t("waitingRoom"));

        if (this.isInteractive) {
            parts.push(t("waitingMiniGameControls"));
        }

        return parts.join(". ");
    }

    private normalizePointer(event: PointerEvent | MouseEvent): LobbyPositionView {
        const stageBounds = this.stage.getBoundingClientRect();
        const viewport = this.getRoomViewport();
        const pointerX = clamp(event.clientX - stageBounds.left, 0, viewport.width);
        const pointerY = clamp(event.clientY - stageBounds.top, 0, viewport.height);

        return {
            x: clamp(
                (viewport.cameraLeft + pointerX) / viewport.worldWidth,
                LOBBY_MIN_X,
                LOBBY_MAX_X
            ),
            y: clamp(
                (viewport.cameraTop + pointerY) / viewport.worldHeight,
                LOBBY_MIN_Y,
                LOBBY_MAX_Y
            )
        };
    }

    private normalizeMovementKey(key: string): "up" | "down" | "left" | "right" | null {
        switch (key) {
            case "ArrowUp":
            case "w":
            case "W":
                return "up";
            case "ArrowDown":
            case "s":
            case "S":
                return "down";
            case "ArrowLeft":
            case "a":
            case "A":
                return "left";
            case "ArrowRight":
            case "d":
            case "D":
                return "right";
            default:
                return null;
        }
    }

    private escapeAttribute(value: string): string {
        return value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }

    private hasMovementIntent(): boolean {
        return this.pressedKeys.size > 0 || this.target !== null;
    }

    private getRoomViewport(): RoomViewport {
        const stageWidth = Math.max(this.stage.clientWidth, 1);
        const stageHeight = Math.max(this.stage.clientHeight, 1);
        const worldScale = this.getWorldScale();
        const worldWidth = Math.max(stageWidth, Math.round(stageWidth * worldScale));
        const worldHeight = Math.max(stageHeight, Math.round(stageHeight * worldScale));
        const cameraAnchor =
            this.localPosition ?? this.authoritativePosition ?? this.getViewerPlayer()?.position ?? null;
        const { cameraLeft, cameraTop } = this.resolveCameraPosition(
            cameraAnchor,
            stageWidth,
            stageHeight,
            worldWidth,
            worldHeight
        );

        return {
            left: 0,
            top: 0,
            width: stageWidth,
            height: stageHeight,
            worldWidth,
            worldHeight,
            cameraLeft,
            cameraTop
        };
    }

    private layoutWorld(viewport: RoomViewport) {
        const layoutStyle = `${viewport.left}:${viewport.top}:${viewport.width}:${viewport.height}`;

        if (this.lastWorldLayoutStyle === layoutStyle) {
            return;
        }

        this.lastWorldLayoutStyle = layoutStyle;
        this.world.style.left = `${viewport.left}px`;
        this.world.style.top = `${viewport.top}px`;
        this.world.style.width = `${viewport.width}px`;
        this.world.style.height = `${viewport.height}px`;
    }

    private getWorldScale(): number {
        if (this.cameraMode !== "follow-viewer") {
            return 1;
        }

        if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
            return window.matchMedia("(max-width: 640px)").matches
                ? PLAYER_VIEWPORT_SCALE_MOBILE
                : PLAYER_VIEWPORT_SCALE;
        }

        return PLAYER_VIEWPORT_SCALE;
    }

    private resolveCameraPosition(
        cameraAnchor: LobbyPositionView | null,
        stageWidth: number,
        stageHeight: number,
        worldWidth: number,
        worldHeight: number
    ): Pick<RoomViewport, "cameraLeft" | "cameraTop"> {
        if (this.cameraMode !== "follow-viewer" || !cameraAnchor) {
            this.resetFollowCamera();
            return {
                cameraLeft: 0,
                cameraTop: 0
            };
        }

        const anchorX = cameraAnchor.x * worldWidth;
        const anchorY = cameraAnchor.y * worldHeight;

        if (!this.followCameraReady) {
            this.followCameraLeft = this.getCenteredCameraOffset(anchorX, stageWidth, worldWidth);
            this.followCameraTop = this.getCenteredCameraOffset(anchorY, stageHeight, worldHeight);
            this.followCameraReady = true;
        }

        // Keep a small deadzone so movement reads on screen before the camera starts yielding.
        this.followCameraLeft = this.getDeadzoneCameraOffset(
            anchorX,
            stageWidth,
            worldWidth,
            this.followCameraLeft,
            FOLLOW_CAMERA_DEADZONE_WIDTH_RATIO
        );
        this.followCameraTop = this.getDeadzoneCameraOffset(
            anchorY,
            stageHeight,
            worldHeight,
            this.followCameraTop,
            FOLLOW_CAMERA_DEADZONE_HEIGHT_RATIO
        );

        return {
            cameraLeft: this.followCameraLeft,
            cameraTop: this.followCameraTop
        };
    }

    private getCenteredCameraOffset(anchor: number, stageSize: number, worldSize: number): number {
        return clamp(anchor - stageSize / 2, 0, Math.max(worldSize - stageSize, 0));
    }

    private getDeadzoneCameraOffset(
        anchor: number,
        stageSize: number,
        worldSize: number,
        currentOffset: number,
        deadzoneRatio: number
    ): number {
        const maxOffset = Math.max(worldSize - stageSize, 0);

        if (maxOffset <= 0) {
            return 0;
        }

        const deadzoneSize = clamp(stageSize * deadzoneRatio, 0, stageSize);
        const deadzoneMargin = (stageSize - deadzoneSize) / 2;
        const clampedOffset = clamp(currentOffset, 0, maxOffset);
        const anchorScreenPosition = anchor - clampedOffset;

        if (anchorScreenPosition < deadzoneMargin) {
            return clamp(anchor - deadzoneMargin, 0, maxOffset);
        }

        if (anchorScreenPosition > stageSize - deadzoneMargin) {
            return clamp(anchor - (stageSize - deadzoneMargin), 0, maxOffset);
        }

        return clampedOffset;
    }

    private resetFollowCamera() {
        this.followCameraLeft = 0;
        this.followCameraTop = 0;
        this.followCameraReady = false;
    }

    private emitPelletSplash(position: LobbyPositionView, timestamp: number) {
        this.worldRenderer.emitPelletSplash(position, timestamp);
    }

    private isEditableTarget(target: EventTarget | null): boolean {
        if (!(target instanceof HTMLElement)) {
            return false;
        }

        if (target.isContentEditable) {
            return true;
        }

        return (
            target instanceof HTMLInputElement ||
            target instanceof HTMLTextAreaElement ||
            target instanceof HTMLSelectElement
        );
    }
}

export const createLobbyPlayground = (
    root: HTMLElement,
    options?: LobbyPlaygroundOptions
): LobbyPlayground => new LobbyPlayground(root, options);
